import { registerTenantData } from "@/Api/tenants";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { useToastNavigate } from "@/utils/toastNavigate";
import { Check, CirclePlus, ImageUp, Loader, Trash } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

export const RegisterBtn = ({ setTenantsData }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    setValue,
  } = useForm();

  const [isOpen, setIsOpen] = useState(false);
  const [isLoad, setIsLoad] = useState(false);
  const { toast } = useToastNavigate();

  const formSubmit = async (data) => {
    setIsLoad((prev) => !prev);
    const payload = {
      ...data,
      logo: data?.logo?.[0] || null,
    };

    const { data: apiResponse, ok } = await registerTenantData(payload);
    if (!ok) {
      setIsLoad((prev) => !prev);
      setIsOpen(false);
      return;
    }

    setTenantsData((prev) => {
      if (prev?.data.length < 15)
        return {
          ...prev,
          data: [apiResponse?.response, ...prev.data],
        };

      prev.data.pop();
      return {
        ...prev,
        data: [apiResponse?.response, ...prev.data],
        last_page: prev.last_page + 1,
      };
    });

    setIsLoad((prev) => !prev);
    setIsOpen(false);

    toast({
      description: `${apiResponse?.response?.name} criada com sucesso!`,
      action: <Check color="#1edc2b" />,
    });
  };
  const formWatch = watch();

  useEffect(() => {
    if (!isOpen) reset();
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className={cn("py-3 flex gap-1", `bg-primary`)}
          onClick={() => setIsOpen(true)}
        >
          <CirclePlus className="size-5" strokeWidth={1.75} />
          Registrar
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] gap-1">
        <DialogHeader className="flex flex-col gap-2">
          <DialogDescription className="text-lg">
            Cadastrar Instituição
          </DialogDescription>
          <DialogTitle className="text-2xl">Informações Básicas</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(formSubmit)}>
          <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-6">
            <div className="sm:col-span-3">
              <div className="sm:col-span-3">
                <div className="flex items-center gap-2">
                  <label
                    htmlFor="username"
                    className="block text-1xl font-medium text-gray-900 dark:text-zinc-300"
                  >
                    Nome:
                  </label>
                  {errors.name && (
                    <span className="text-sm text-red-400">
                      {errors?.name?.message as string}
                    </span>
                  )}
                </div>
                <Input
                  {...register("name", { required: "*Obrigatório" })}
                  className="mt-1"
                  placeholder="Insira um nome"
                />
              </div>
            </div>
            <div className="sm:col-span-3">
              <div className="sm:col-span-5">
                <div className="flex items-center gap-2">
                  <label
                    htmlFor="document"
                    className="block text-1xl font-medium text-gray-900 dark:text-zinc-300"
                  >
                    Documento:
                  </label>
                  {errors.document && (
                    <span className="text-sm text-red-400">
                      {errors.document.message as string}
                    </span>
                  )}
                </div>
                <Input
                  {...register("document", {
                    required: "*Obrigatório",
                    minLength: {
                      value: 14,
                      message: "O mínimo de dígitos é 9",
                    },
                  })}
                  className="mt-1"
                  typeMask="cpfOrCnpj"
                  placeholder="Insira o número de documentação"
                />
              </div>
            </div>
            <div className="sm:col-span-3">
              <div className="sm:col-span-5">
                <div className="flex items-center gap-2">
                  <label
                    htmlFor="email"
                    className="block text-1xl font-medium text-gray-900 dark:text-zinc-300"
                  >
                    Email:
                  </label>
                  {errors.email && (
                    <span className="text-sm text-red-400">
                      {errors.email.message as string}
                    </span>
                  )}
                </div>
                <Input
                  {...register("email", {
                    required: "*Obrigatório",
                    pattern: {
                      value: /^\S+@\S+$/i,
                      message: "Formato inválido.",
                    },
                  })}
                  className="mt-1"
                  placeholder="Insira o e-mail"
                />
              </div>
            </div>
            <div className="sm:col-span-3">
              <div className="sm:col-span-5">
                <div className="flex items-center gap-2">
                  <label
                    htmlFor="phone"
                    className="block text-1xl font-medium text-gray-900 dark:text-zinc-300"
                  >
                    Telefone:
                  </label>
                  {errors.phone && (
                    <span className="text-sm text-red-400">
                      {errors.phone.message as string}
                    </span>
                  )}
                </div>
                <Input
                  {...register("phone", {
                    required: "*Obrigatório",
                    minLength: {
                      value: 15,
                      message: "O mínimo de dígitos é 9",
                    },
                  })}
                  typeMask="phone"
                  className="mt-1 outline-cyan-800"
                  placeholder="Insira o número para contato"
                />
              </div>
            </div>
          </div>
          <div className="mt-5">
            <label className="text-1xl font-medium text-gray-900 dark:text-zinc-300">
              Foto:
            </label>
            <div className="flex justify-center itens-center rounded-md border border-dashed dark:border-gray-500 border-gray-900/25 px-2 py-3 mt-2">
              <div className="flex flex-col items-center gap-4">
                {formWatch?.logo?.length > 0 ? (
                  <img
                    className="rounded-md aspect-square w-40 object-cover"
                    src={URL.createObjectURL(formWatch?.logo?.[0])}
                    alt="image_logo"
                  />
                ) : (
                  <ImageUp className="mx-auto size-12 text-gray-300 mt-2" />
                )}
                <div className="flex justify-center items-center gap-5 text-sm/6 text-gray-600">
                  <label
                    htmlFor="file-upload"
                    className="relative cursor-pointer rounded-md bg-transparent font-semibold text-blue-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2 hover:text-blue-700"
                  >
                    <span className="flex items-center gap-1">
                      {formWatch?.logo?.length > 0 && (
                        <ImageUp className="size-5" />
                      )}
                      Procurar imagem
                    </span>
                    <input
                      id="file-upload"
                      type="file"
                      className="sr-only"
                      accept="image/*"
                      {...register("logo")}
                    />
                  </label>
                  {formWatch?.logo && formWatch?.logo?.length > 0 && (
                    <Button
                      className="h-6 px-0 bg-transparent text-slate font-medium hover:text-red-600 hover:bg-transparent flex gap-1"
                      onClick={() => setValue("logo", null)}
                    >
                      <Trash className="size-5" />
                      Remover
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="mt-5">
            <Button
              type="submit"
              className={cn("flex gap-2", colors.bg_primary)}
              disabled={isLoad}
            >
              {isLoad ? (
                <>
                  <Loader className="animate-spin size-4" />
                  Cadastrando
                </>
              ) : (
                <>Cadastrar</>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
